import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabaseClient';
import { diasDelMes, type PosicionPlan } from '../domain/planningBoard';
import { dayStatus, type DayStatus } from '../domain/dayStatus';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';

const TZ = 'America/Lima';

/**
 * PostgREST corta la respuesta en 1000 filas EN SILENCIO: no avisa, no da error, simplemente
 * devuelve menos. Un mes con varias campañas pasa ese tope de sobra (una campaña de 20 tiendas
 * × 26 días ya son 520 posiciones), así que TODA lectura del tablero pagina con `.range()`.
 */
const PAGE_SIZE = 1000;

interface PaginaPostgrest<T> {
  data: T[] | null;
  error: { message: string } | null;
  count: number | null;
}

/**
 * Lee todas las páginas de una consulta y compara el total recibido contra el `count: 'exact'`
 * del servidor. Si no cuadran, LANZA: un tablero con menos posiciones de las que hay muestra
 * huecos que no existen (o esconde los que sí), y sobre eso se toman decisiones de operación.
 */
async function leerTodo<T>(
  pagina: (desde: number, hasta: number) => PromiseLike<PaginaPostgrest<T>>,
  queSeLeia: string,
): Promise<T[]> {
  const filas: T[] = [];
  let total: number | null = null;
  for (let desde = 0; ; desde += PAGE_SIZE) {
    const { data, error, count } = await pagina(desde, desde + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    if (count !== null) total = count;
    const lote = data ?? [];
    filas.push(...lote);
    if (lote.length < PAGE_SIZE) break;
  }
  if (total !== null && filas.length !== total) {
    throw new Error(
      `Lectura incompleta de ${queSeLeia}: se recibieron ${filas.length} de ${total} filas. ` +
      'No se muestra el tablero para no dar números falsos: filtra por campaña y reintenta.',
    );
  }
  return filas;
}

/** Rango cerrado del mes, en fechas de Lima tal como están guardadas en `positions.date`. */
function rangoDelMes(anio: number, mes: number): { desde: string; hasta: string } {
  const dias = diasDelMes(anio, mes);
  return { desde: dias[0], hasta: dias[dias.length - 1] };
}

interface AsignacionRaw {
  status: string;
  attendance: { kind: string }[] | null;
}

interface PosicionRaw {
  id: string;
  campaign_id: string;
  date: string;
  assignments: AsignacionRaw[] | null;
}

/** La asignación vigente de la posición: solo `aprobada` activa el turno y habilita marcar. */
const aprobadaDe = <T extends { status: string }>(asignaciones: T[] | null): T | null =>
  (asignaciones ?? []).find((a) => a.status === 'aprobada') ?? null;

/**
 * Todas las posiciones del mes, aplanadas para el tablero.
 *
 * Se traen las posiciones (lo PROGRAMADO) con su asignación y sus marcados, de modo que el
 * mismo dato sirva para las dos métricas del interruptor sin volver a consultar.
 */
export async function fetchPosicionesMes(
  anio: number, mes: number, campaignId?: string,
): Promise<PosicionPlan[]> {
  const { desde: desdeFecha, hasta: hastaFecha } = rangoDelMes(anio, mes);

  const filas = await leerTodo<PosicionRaw>((desde, hasta) => {
    let q = supabase
      .from('positions')
      // `campaigns!inner` + el filtro de archivadas: las FILAS de la matriz salen de las
      // POSICIONES, no del listado de campañas, así que filtrar aquel no alcanzaba — una
      // campaña archivada seguía apareciendo como fila (con etiqueta "Campaña <id>", porque su
      // nombre ya no venía en la lista). Es un tercer lugar donde se listan campañas de hecho.
      .select(
        'id, campaign_id, date, campaign:campaigns!inner(archived_at), assignments(status, attendance(kind))',
        { count: 'exact' },
      )
      .is('campaign.archived_at', null)
      // 0041: el banco de pruebas no entra en la planificación. Sus jornadas son ensayos y
      // contarlas inflaría la cobertura del mes con trabajo que nadie hizo.
      .eq('campaign.es_banco_pruebas', false)
      .gte('date', desdeFecha)
      .lte('date', hastaFecha);
    if (campaignId) q = q.eq('campaign_id', campaignId);
    // El orden por `id` es el desempate que hace estable el paginado: sin él, dos páginas
    // pueden repetir una fila y omitir otra.
    return q.order('date').order('id').range(desde, hasta) as unknown as
      PromiseLike<PaginaPostgrest<PosicionRaw>>;
  }, 'posiciones del mes');

  return filas.map((f) => {
    const aprobada = aprobadaDe(f.assignments);
    return {
      campaignId: f.campaign_id,
      date: f.date,
      tieneAprobada: aprobada !== null,
      marcoIngreso: (aprobada?.attendance ?? []).some((m) => m.kind === 'ingreso'),
    };
  });
}

// --- Detalle de una celda: campaña × día, tienda por tienda ----------------

export interface DetalleDia {
  pdvCode: string; pdvName: string; startTime: string; endTime: string;
  workerName: string | null; workerDni: string | null;
  estado: 'sin_asignar' | 'asignado' | 'ingreso' | 'completo' | 'falta';
  horaIngreso: string | null; horaSalida: string | null; flags: string[];
}

/** `dayStatus` es la misma lógica que usa el tablero de Hoy; aquí solo se renombra. */
const ESTADO_DETALLE: Record<DayStatus, DetalleDia['estado']> = {
  sin_asignar: 'sin_asignar',
  esperando: 'asignado',
  ingresado: 'ingreso',
  completo: 'completo',
  falta: 'falta',
};

interface MarcadoRaw {
  kind: string;
  device_ts: string;
  flags: string[] | null;
}

interface DetalleRaw {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  pdv: { code: string; name: string } | null;
  assignments: {
    status: string;
    /** Va en el select para que `completarTrabajadores` sepa a quién buscar (supervisor, 0218). */
    worker_id: string | null;
    worker: { dni: string; first_names: string; last_names: string } | null;
    attendance: MarcadoRaw[] | null;
  }[] | null;
}

const hhmm = (t: string): string => t.slice(0, 5);
const horaLima = (ts: string): string => formatInTimeZone(ts, TZ, 'HH:mm');

/**
 * Los puestos de una campaña en un día: tienda, horario, recurso, estado y alertas.
 * Es lo que se abre al hacer clic en una celda del tablero.
 */
export async function fetchDetalleDia(campaignId: string, date: string): Promise<DetalleDia[]> {
  const filas = await leerTodo<DetalleRaw>((desde, hasta) => supabase
    .from('positions')
    .select(
      // Hint de FK obligatorio: `assignments` tiene tres claves foráneas a `profiles`.
      `id, date, start_time, end_time, slot_index,
      pdv:pdv(code, name),
      assignments(status, worker_id,
        worker:profiles!assignments_worker_id_fkey(dni, first_names, last_names),
        attendance(kind, device_ts, flags))`,
      { count: 'exact' },
    )
    .eq('campaign_id', campaignId)
    .eq('date', date)
    .order('start_time')
    .order('id')
    .range(desde, hasta) as unknown as PromiseLike<PaginaPostgrest<DetalleRaw>>,
  'posiciones del día');

  // El supervisor de ruta no lee `profiles` (0218): su embed llega en null y se completa acá con
  // nombre y sin DNI. Para quien sí lee `profiles` no falta ninguno y no hay llamada extra.
  await completarTrabajadores(filas.flatMap((f) => f.assignments ?? []));

  const nowLima = formatInTimeZone(new Date(), TZ, "yyyy-MM-dd'T'HH:mm:ss");

  const rows: (DetalleDia & { slotIndex: number })[] = filas.map((f) => {
    const aprobada = aprobadaDe(f.assignments);
    const marcados = aprobada?.attendance ?? [];
    // Con reintentos puede haber más de un marcado del mismo tipo: vale el primer ingreso
    // (la hora real de llegada) y la última salida (cuándo dejó la tienda).
    const ingresos = marcados.filter((m) => m.kind === 'ingreso').map((m) => m.device_ts).sort();
    const salidas = marcados.filter((m) => m.kind === 'salida').map((m) => m.device_ts).sort();

    const estado = ESTADO_DETALLE[dayStatus({
      hasApprovedAssignment: aprobada !== null,
      hasEntry: ingresos.length > 0,
      hasExit: salidas.length > 0,
      date: f.date,
      endTime: f.end_time,
      nowLima,
    })];

    return {
      pdvCode: f.pdv?.code ?? '',
      pdvName: f.pdv?.name ?? '—',
      startTime: hhmm(f.start_time),
      endTime: hhmm(f.end_time),
      workerName: aprobada?.worker
        ? `${aprobada.worker.first_names} ${aprobada.worker.last_names}`
        : null,
      // `||` y no `??`: el trabajador completado para el supervisor trae `dni: ''` (no lo ve), y
      // así la columna muestra «—» en vez de una celda en blanco. `profiles.dni` es NOT NULL.
      workerDni: aprobada?.worker?.dni || null,
      estado,
      horaIngreso: ingresos.length > 0 ? horaLima(ingresos[0]) : null,
      horaSalida: salidas.length > 0 ? horaLima(salidas[salidas.length - 1]) : null,
      flags: [...new Set(marcados.flatMap((m) => m.flags ?? []))],
      slotIndex: f.slot_index,
    };
  });

  // Por tienda y horario: así la lista se recorre como la ruta del día del coordinador.
  rows.sort((a, b) => a.pdvCode.localeCompare(b.pdvCode)
    || a.startTime.localeCompare(b.startTime)
    || a.slotIndex - b.slotIndex);

  return rows.map(({ slotIndex: _slotIndex, ...row }) => row);
}

// --- Campañas del mes para el filtro ---------------------------------------

interface CampaniaRaw {
  id: string;
  /** Nombre propio de la campaña (0028). Cuando existe, es la etiqueta de la fila. */
  name: string | null;
  client: string | null;
  brand: string | null;
}

/**
 * Campañas cuyo periodo se cruza con el mes, con la etiqueta "<marca> - <cliente>"
 * (la misma forma que usa el jefe de operaciones al hablar: "Makro Anual - P&G").
 */
export async function listCampaniasDelMes(
  anio: number, mes: number,
): Promise<{ id: string; etiqueta: string }[]> {
  const { desde, hasta } = rangoDelMes(anio, mes);

  const filas = await leerTodo<CampaniaRaw>((from, to) => supabase
    .from('campaigns')
    .select('id, name, client, brand', { count: 'exact' })
    // Las archivadas no aparecen en la matriz del mes (0027). Sin este filtro, esconderlas de
    // Campañas no serviría de nada: seguirían saliendo acá con toda su grilla.
    .is('archived_at', null)
    .eq('es_banco_pruebas', false)
    .lte('start_date', hasta)
    .gte('end_date', desde)
    .order('brand')
    .order('client')
    .order('id')
    .range(from, to) as unknown as PromiseLike<PaginaPostgrest<CampaniaRaw>>,
  'campañas del mes');

  return filas.map((c) => ({
    id: c.id,
    // El nombre propio manda: cuatro campañas distintas de Pringles serían las cuatro
    // "Pringles - Yichang" y la matriz del mes no se podría leer.
    etiqueta: c.name?.trim() || [c.brand, c.client]
      .map((s) => (s ?? '').trim())
      .filter((s) => s !== '')
      .join(' - ') || '(campaña sin nombre)',
  }));
}
